/* ==========================================================================
   Sharky — cosy purchase components
   <sk-buy>   one product: pick options, see the real price and stock, add to cart.
   <sk-pair>  two onesies with their own sizes, added together (Buy 2 discount shown, never invented).
   <sk-combo> a 2- or 3-piece set of different products, each with its own options (combo saving mirrors the store discount).

   Both talk to the theme the same way Horizon's own product form does: they dispatch the standard
   cart-lines-update event (so the cart count and the cart drawer update and open by themselves) and
   post to the theme's cart-add route. No third-party libraries. The pages render a complete, working
   form without this file: it posts to /cart/add.
   ========================================================================== */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const text = (node, value) => {
  if (node && node.textContent !== value) node.textContent = value;
};

const money = (cents, currency, locale) => {
  try {
    return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency }).format(cents / 100);
  } catch (error) {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
};

/** Pull the freshest cart so the drawer can render it. */
async function refreshCart() {
  const items = document.querySelector('cart-items-component');
  if (items && typeof items.fetchCartData === 'function') {
    await customElements.whenDefined('cart-items-component');
    return items.fetchCartData();
  }
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
  return response.json();
}

/**
 * Add lines to the cart the way the theme's own product form does.
 * @param {HTMLElement} source - element that dispatches the standard event
 * @param {Array<{id: number|string, quantity: number}>} lines
 * @returns {Promise<{ok: boolean, message?: string}>}
 */
async function addLines(source, lines) {
  const sections = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);
  const total = lines.reduce((sum, line) => sum + line.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  const finish = (didError, cart, extra = {}) =>
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'sk-buy', sourceId: source.id || '', itemCount: total, didError, ...extra },
    });

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: lines.map((line) => ({ id: Number(line.id), quantity: line.quantity })),
        sections: sections.join(','),
      }),
    });
    const data = await response.json();

    if (data.status) {
      const message = data.description || data.message || 'This item could not be added.';
      source.dispatchEvent(
        new CartErrorEvent({
          error: data.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: data.description, errors: data.errors },
        })
      );
      refreshCart().then((cart) => finish(true, cart)).catch(deferred.reject);
      return { ok: false, message };
    }

    const cart = await refreshCart();
    finish(false, cart, { sections: data.sections });
    return { ok: true };
  } catch (error) {
    deferred.reject(error);
    source.dispatchEvent(
      new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
    );
    return { ok: false, message: 'We could not reach the shop just now. Please try again.' };
  }
}

/** Shared add-to-cart button behaviour: busy, success, error. */
function bindAddButton(host, button, errorBox, live, getLines) {
  const label = button.querySelector('[data-sc-add-label]');
  let timer = 0;

  const reset = () => {
    button.classList.remove('is-done', 'is-busy');
    button.removeAttribute('aria-busy');
    if (label) label.textContent = button.dataset.label || label.textContent;
  };

  button.addEventListener('click', async (event) => {
    event.preventDefault();
    if (button.disabled || button.classList.contains('is-busy')) return;
    const lines = getLines();
    if (!lines || !lines.length) return;

    window.clearTimeout(timer);
    errorBox.hidden = true;
    button.classList.add('is-busy');
    button.setAttribute('aria-busy', 'true');

    const result = await addLines(host, lines);

    button.classList.remove('is-busy');
    button.removeAttribute('aria-busy');

    if (result.ok) {
      button.classList.add('is-done');
      if (label) label.textContent = host.dataset.addedLabel || 'Added to cart';
      live.textContent = host.dataset.addedLabel || 'Added to cart';
      timer = window.setTimeout(() => {
        reset();
        live.textContent = '';
      }, 2200);
    } else {
      errorBox.textContent = result.message || 'This item could not be added.';
      errorBox.hidden = false;
    }
  });
}

/* ------------------------------------------------------------------------ */
class SkBuy extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    const json = this.querySelector('script[data-sc-json]');
    const form = this.querySelector('[data-sc-form]');
    if (!json || !form) return;

    try {
      this.product = JSON.parse(json.textContent);
    } catch (error) {
      return;
    }

    this.ready = true;
    this.form = form;
    this.idInput = form.querySelector('[data-sc-id]');
    this.groups = Array.from(form.querySelectorAll('[data-sc-option]'));
    this.priceBox = form.querySelector('[data-sc-pricebox]');
    this.priceEl = form.querySelector('[data-sc-price]');
    this.compareEl = form.querySelector('[data-sc-compare]');
    this.statusEl = form.querySelector('[data-sc-status]');
    this.addButton = form.querySelector('[data-sc-add]');
    this.addLabel = form.querySelector('[data-sc-add-label]');
    this.details = form.querySelector('[data-sc-details]');
    this.errorBox = form.querySelector('[data-sc-error]');
    this.live = form.querySelector('[data-sc-live]');
    this.image = this.querySelector('[data-sc-image][data-sc-swap]');
    this.caption = this.querySelector('[data-sc-caption]');
    this.required = new Set((form.dataset.require || '').split(',').filter(Boolean).map(Number));

    const initial = this.product.variants.find((variant) => String(variant.id) === this.idInput.value);
    this.selection = initial ? [...initial.options] : this.product.options.map((option) => option.values[0]);
    // Options the shopper must choose themselves (sizes) start empty, so nobody orders a default size by accident.
    this.required.forEach((index) => {
      this.selection[index] = null;
    });
    this.current = null;

    form.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('[data-sc-value]')) return;
      const index = Number(input.closest('[data-sc-option]').dataset.scOption);
      this.selection[index] = input.value;
      this.errorBox.hidden = true;
      this.resolve(index);
      this.render();
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.addButton?.click();
    });

    // Not-yet-chosen sizes: a friendly nudge instead of a dead button.
    this.addButton?.addEventListener(
      'click',
      (event) => {
        if (this.complete()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.errorBox.textContent = this.addButton.dataset.pendingMessage || 'Please choose your size first.';
        this.errorBox.hidden = false;
        const group = this.groups.find((g) => this.selection[Number(g.dataset.scOption)] === null);
        group?.querySelector('input:not(:disabled)')?.focus();
      },
      true
    );

    bindAddButton(this, this.addButton, this.errorBox, this.live, () =>
      this.complete() && this.current && this.current.available ? [{ id: this.current.id, quantity: 1 }] : null
    );

    this.render(true);
  }

  complete() {
    return this.selection.every((value) => value !== null);
  }

  /** Variants that agree with every option the shopper has already chosen (empty ones are wildcards). */
  matching(selection, upTo = selection.length - 1) {
    return this.product.variants.filter((variant) =>
      variant.options.every((value, index) => index > upTo || selection[index] === null || value === selection[index])
    );
  }

  variantFor(selection) {
    const list = this.matching(selection);
    return list.find((variant) => variant.available) || list[0];
  }

  /** After a change in option `index`, make the later options valid again. */
  resolve(index) {
    if (this.matching(this.selection).length) return;
    const candidates = this.matching(this.selection, index);
    const pick = candidates.find((variant) => variant.available) || candidates[0];
    if (!pick) return;
    this.selection = this.selection.map((value, i) => (i <= index || this.required.has(i) ? (i <= index ? value : null) : pick.options[i]));
  }

  render(first = false) {
    const variant = this.variantFor(this.selection);
    const previous = this.current;
    const complete = this.complete();
    this.current = variant || null;
    if (!variant) return;

    this.idInput.value = complete ? variant.id : '';

    // Checked state and which values can still be chosen
    this.groups.forEach((group) => {
      const index = Number(group.dataset.scOption);
      const chosen = this.selection[index];
      text(group.querySelector('[data-sc-current]'), chosen === null ? group.dataset.placeholder || '' : chosen);
      group.querySelectorAll('[data-sc-value]').forEach((input) => {
        input.checked = chosen !== null && input.value === chosen;
        const probe = [...this.selection];
        probe[index] = input.value;
        input.disabled = !this.matching(probe, index).some((candidate) => candidate.available);
      });
    });

    // Price and stock. With sizes still open and different prices ahead, say "From".
    const pool = complete ? [variant] : this.matching(this.selection);
    const low = Math.min(...pool.map((candidate) => candidate.price));
    const high = Math.max(...pool.map((candidate) => candidate.price));
    const lowVariant = pool.find((candidate) => candidate.price === low) || variant;
    const priceText = !complete && high > low ? `${this.priceEl.dataset.from || 'From'} ${lowVariant.price_fmt}` : lowVariant.price_fmt;
    const changed = !previous || previous.price !== variant.price || previous.complete !== complete;
    if (changed && !first && this.priceBox && !reducedMotion.matches) {
      this.priceBox.classList.add('is-changing');
      window.setTimeout(() => this.priceBox.classList.remove('is-changing'), 180);
    }
    text(this.priceEl, priceText);
    if (this.compareEl) {
      const onSale = complete && variant.compare_at > variant.price;
      this.compareEl.hidden = !onSale;
      if (onSale) text(this.compareEl, variant.compare_fmt);
    }
    if (this.statusEl) {
      if (complete) {
        this.statusEl.hidden = false;
        this.statusEl.classList.toggle('is-out', !variant.available);
        text(this.statusEl, variant.available ? this.statusEl.dataset.in : this.statusEl.dataset.out);
      } else {
        this.statusEl.hidden = true;
      }
    }
    if (this.addButton) {
      this.addButton.disabled = complete && !variant.available;
      this.addButton.setAttribute('aria-disabled', String(!complete || !variant.available));
      if (this.addLabel && !this.addButton.classList.contains('is-done')) {
        text(
          this.addLabel,
          !complete ? this.addButton.dataset.choose || this.addButton.dataset.label : variant.available ? this.addButton.dataset.label : this.statusEl?.dataset.out || 'Sold out'
        );
      }
    }
    if (this.details) {
      const url = new URL(this.details.href, window.location.origin);
      url.searchParams.set('variant', variant.id);
      this.details.href = url.pathname + url.search;
    }

    // Photo of the real variant
    if (this.image && variant.image && this.image.dataset.src !== variant.image) {
      this.swapImage(variant, first);
    }
    if (this.caption) text(this.caption, variant.options.length > 1 ? variant.options[0] : variant.title);
    variant.complete = complete;
    this.dispatchEvent(new CustomEvent('sk-buy:change', { detail: { variant, complete }, bubbles: true }));
  }

  swapImage(variant, first) {
    const img = this.image;
    const base = variant.image;
    const set = () => {
      img.src = base;
      img.srcset = [480, 720, 960, 1200].map((w) => `${base.replace(/width=\d+/, `width=${w}`)} ${w}w`).join(', ');
      img.alt = variant.alt || '';
      img.dataset.src = base;
      if (variant.image_w && variant.image_h) {
        img.width = variant.image_w;
        img.height = variant.image_h;
      }
      img.classList.remove('is-swapping');
    };
    if (first || reducedMotion.matches) {
      set();
      return;
    }
    img.classList.add('is-swapping');
    const probe = new Image();
    probe.onload = probe.onerror = () => window.setTimeout(set, 120);
    probe.src = base;
  }
}

/* ------------------------------------------------------------------------ */
class SkPair extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    const json = this.querySelector('script[data-sc-json]');
    this.selects = Array.from(this.querySelectorAll('[data-sc-size]'));
    this.addButton = this.querySelector('[data-sc-add]');
    if (!json || this.selects.length < 2 || !this.addButton) return;
    try {
      this.product = JSON.parse(json.textContent);
    } catch (error) {
      return;
    }
    this.ready = true;
    this.pct = Number(this.dataset.discount || 0);
    this.totalEl = this.querySelector('[data-sc-total]');
    this.regularEl = this.querySelector('[data-sc-regular]');
    this.saveEl = this.querySelector('[data-sc-save]');
    this.priceBox = this.querySelector('[data-sc-pricebox]');
    this.errorBox = this.querySelector('[data-sc-error]');
    this.live = this.querySelector('[data-sc-live]');
    this.addLabel = this.querySelector('[data-sc-add-label]');
    this.base = Math.min(...this.product.variants.map((variant) => variant.price));

    this.selects.forEach((select) =>
      select.addEventListener('change', () => {
        this.errorBox.hidden = true;
        this.render();
      })
    );
    this.querySelector('form')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.addButton.click();
    });
    this.addButton.addEventListener(
      'click',
      (event) => {
        if (this.complete()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.errorBox.textContent = this.dataset.pending || 'Please choose a size for each person.';
        this.errorBox.hidden = false;
        this.selects.find((select) => !select.value)?.focus();
      },
      true
    );
    bindAddButton(this, this.addButton, this.errorBox, this.live, () => this.lines());
    this.render(true);
  }

  variants() {
    return this.selects.map((select) => this.product.variants.find((variant) => String(variant.id) === select.value));
  }

  complete() {
    return this.variants().every((variant) => variant && variant.available);
  }

  lines() {
    if (!this.complete()) return null;
    const counts = new Map();
    this.variants().forEach((variant) => counts.set(variant.id, (counts.get(variant.id) || 0) + 1));
    return [...counts].map(([id, quantity]) => ({ id, quantity }));
  }

  render(first = false) {
    const chosen = this.variants();
    const complete = this.complete();
    // The store discounts each unit and rounds the discount down to a whole cent; mirror that exactly.
    const unit = (variant) => (variant ? variant.price : this.base);
    const regular = chosen.reduce((sum, variant) => sum + unit(variant), 0);
    const total = chosen.reduce((sum, variant) => sum + unit(variant) - Math.floor((unit(variant) * this.pct) / 100), 0);
    const { currency, locale } = this.product;

    if (!first && this.priceBox && !reducedMotion.matches) {
      this.priceBox.classList.add('is-changing');
      window.setTimeout(() => this.priceBox.classList.remove('is-changing'), 180);
    }
    text(this.totalEl, money(total, currency, locale));
    const saves = total < regular;
    if (this.regularEl) {
      this.regularEl.hidden = !saves;
      text(this.regularEl, money(regular, currency, locale));
    }
    if (this.saveEl) {
      this.saveEl.hidden = !saves;
      this.saveEl.textContent = (this.dataset.saveTemplate || 'You save {amount}').replace('{amount}', money(regular - total, currency, locale));
    }
    this.addButton.setAttribute('aria-disabled', String(!complete));
    if (this.addLabel && !this.addButton.classList.contains('is-done')) {
      text(this.addLabel, complete ? this.addButton.dataset.label : this.addButton.dataset.choose || this.addButton.dataset.label);
    }
  }
}

/* ------------------------------------------------------------------------ */
/**
 * <sk-combo>: a set of 2 or 3 DIFFERENT products, each with its own options, added together.
 * Prices, options and stock come from the real products. The saving shown is the store's own automatic
 * discount ("Combo of 2" / "Combo of 3"), mirrored exactly: Shopify takes the percentage off each unit and
 * rounds that amount down to a whole cent. If the percentage in the theme settings differs from the store
 * discount, the cart (not this component) is the truth, so keep the two equal.
 */
const REQUIRED_OPTION = /size|height|length|width|waist|fit/i;
const SINGLE_OPTION = (option) => option.values.length === 1 && option.values[0] === 'Default Title';

class SkCombo extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    const json = this.querySelector('script[data-scb-json]');
    this.slotEls = Array.from(this.querySelectorAll('[data-scb-slot]'));
    this.addButton = this.querySelector('[data-sc-add]');
    if (!json || this.slotEls.length < 2 || !this.addButton) return;
    try {
      this.products = JSON.parse(json.textContent);
    } catch (error) {
      return;
    }
    if (!Array.isArray(this.products) || this.products.length < 2) return;

    this.ready = true;
    this.uid = Math.random().toString(36).slice(2, 7);
    this.pct = { 2: Number(this.dataset.pctTwo || 0), 3: Number(this.dataset.pctThree || 0) };
    this.max = Math.min(3, this.slotEls.length, this.products.length);
    this.count = Math.min(this.max, Number(this.dataset.count) || 2);
    this.meta = [];
    this.slotEls[0].querySelectorAll('[data-scb-pick]').forEach((input) => {
      this.meta[Number(input.value)] = { label: input.dataset.label || '', featured: input.dataset.featured || '' };
    });
    this.countInputs = Array.from(this.querySelectorAll('[data-scb-count]'));
    this.presets = Array.from(this.querySelectorAll('[data-scb-preset]'));
    this.listEl = this.querySelector('[data-scb-list]');
    this.priceBox = this.querySelector('[data-sc-pricebox]');
    this.totalEl = this.querySelector('[data-sc-total]');
    this.regularEl = this.querySelector('[data-scb-regular]');
    this.saveEl = this.querySelector('[data-scb-save]');
    this.hintEl = this.querySelector('[data-scb-hint]');
    this.errorBox = this.querySelector('[data-sc-error]');
    this.live = this.querySelector('[data-sc-live]');
    this.addLabel = this.querySelector('[data-sc-add-label]');
    this.slots = this.slotEls.map(() => ({ p: -1, sel: [] }));
    this.lastAnnounce = '';

    this.addEventListener('change', (event) => this.onChange(event));
    this.presets.forEach((button) =>
      button.addEventListener('click', () => {
        if (this.applyPreset(button.dataset.scbPreset.split(','))) {
          this.errorBox.hidden = true;
          this.render();
        }
      })
    );

    // A combo that is not complete explains itself instead of being a dead button.
    this.addButton.addEventListener(
      'click',
      (event) => {
        if (this.lines()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.errorBox.textContent = this.dataset.pending || 'Please choose your options for every piece first.';
        this.errorBox.hidden = false;
        const gap = this.firstGap();
        if (gap) {
          gap.classList.add('is-missing');
          gap.focus();
        }
      },
      true
    );
    bindAddButton(this, this.addButton, this.errorBox, this.live, () => this.lines());

    // Start from ?combo=handle,handle (used by product-page links), else the first usable ready-made combo.
    const wanted = new URLSearchParams(window.location.search).get('combo');
    let started = wanted ? this.applyPreset(wanted.split(',')) : false;
    if (!started) started = this.presets.some((button) => this.applyPreset(button.dataset.scbPreset.split(',')));
    if (!started) {
      this.setCount(this.count);
    }
    this.render(true);
  }

  /* ---- state ------------------------------------------------------------ */
  sellable(p) {
    return this.products[p].variants.some((variant) => variant.available);
  }

  initialSelection(product) {
    const lead = product.variants.find((variant) => variant.available) || product.variants[0];
    // Sizes (and other measures) are never guessed; things like colour start on a real, in-stock value.
    return product.options.map((option, index) => (REQUIRED_OPTION.test(option.name) ? null : lead.options[index]));
  }

  assign(k, p) {
    const slot = this.slots[k];
    if (slot.p === p) return;
    slot.p = p;
    slot.sel = this.initialSelection(this.products[p]);
  }

  ensureSlot(k) {
    const slot = this.slots[k];
    const used = this.slots.slice(0, this.count).map((other, i) => (i === k ? -1 : other.p));
    if (slot.p >= 0 && !used.includes(slot.p) && this.sellable(slot.p)) return;
    const next = this.products.findIndex((_, p) => !used.includes(p) && this.sellable(p));
    slot.p = -1;
    if (next >= 0) this.assign(k, next);
  }

  setCount(n) {
    this.count = Math.max(2, Math.min(this.max, n));
    for (let k = 0; k < this.count; k += 1) this.ensureSlot(k);
  }

  applyPreset(handles) {
    const picks = handles.map((handle) => this.products.findIndex((product) => product.handle === handle));
    if (
      picks.length < 2 ||
      picks.length > this.max ||
      picks.some((p) => p < 0 || !this.sellable(p)) ||
      new Set(picks).size !== picks.length
    ) {
      return false;
    }
    this.count = picks.length;
    picks.forEach((p, k) => this.assign(k, p));
    for (let k = picks.length; k < this.slots.length; k += 1) {
      if (picks.includes(this.slots[k].p)) this.slots[k] = { p: -1, sel: [] };
    }
    return true;
  }

  matching(product, selection) {
    return product.variants.filter((variant) => variant.options.every((value, i) => selection[i] === null || value === selection[i]));
  }

  /** After one option changed, make the others valid again (a colour may not come in every size). */
  repair(slot, changed) {
    const product = this.products[slot.p];
    if (this.matching(product, slot.sel).length) return;
    product.options.forEach((option, j) => {
      if (j !== changed && !this.matching(product, slot.sel).length) slot.sel[j] = null;
    });
    product.options.forEach((option, j) => {
      if (slot.sel[j] === null && !REQUIRED_OPTION.test(option.name)) {
        const pool = this.matching(product, slot.sel);
        const pick = pool.find((variant) => variant.available) || pool[0];
        if (pick) slot.sel[j] = pick.options[j];
      }
    });
  }

  resolve(slot) {
    const product = this.products[slot.p];
    const pool = this.matching(product, slot.sel);
    const complete = slot.sel.every((value) => value !== null);
    return { product, pool, complete, variant: pool.find((variant) => variant.available) || pool[0] || null };
  }

  rows() {
    return this.slots.slice(0, this.count).map((slot) => (slot.p < 0 ? null : this.resolve(slot)));
  }

  lines() {
    const rows = this.rows();
    if (!rows.every((row) => row && row.complete && row.variant && row.variant.available)) return null;
    return rows.map((row) => ({ id: row.variant.id, quantity: 1 }));
  }

  firstGap() {
    for (let k = 0; k < this.count; k += 1) {
      const slot = this.slots[k];
      const el = this.slotEls[k];
      if (slot.p < 0) return el.querySelector('[data-scb-pick]:not(:disabled)');
      const index = slot.sel.findIndex((value) => value === null);
      if (index >= 0) return el.querySelector(`select[data-scb-opt="${index}"]`);
    }
    return null;
  }

  /* ---- events ----------------------------------------------------------- */
  onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.matches('[data-scb-count]')) {
      this.setCount(Number(target.value));
      this.errorBox.hidden = true;
      this.render();
      return;
    }
    const el = target.closest('[data-scb-slot]');
    if (!el) return;
    const i = this.slotEls.indexOf(el);
    if (i < 0) return;
    if (target.matches('[data-scb-pick]')) {
      this.assign(i, Number(target.value));
    } else if (target.matches('select[data-scb-opt]')) {
      const k = Number(target.dataset.scbOpt);
      this.slots[i].sel[k] = target.value === '' ? null : target.value;
      target.classList.remove('is-missing');
      this.repair(this.slots[i], k);
    } else {
      return;
    }
    this.errorBox.hidden = true;
    this.render();
  }

  /* ---- drawing ---------------------------------------------------------- */
  render(first = false) {
    this.dataset.count = String(this.count);
    this.countInputs.forEach((input) => {
      input.checked = Number(input.value) === this.count;
    });
    this.slotEls.forEach((el, i) => this.renderSlot(i, first));
    this.renderPresets();
    this.renderSummary(first);
  }

  renderSlot(i, first) {
    const el = this.slotEls[i];
    const slot = this.slots[i];
    const active = i < this.count;
    if (el.hidden === active) el.hidden = !active; // touch only on change, so the entrance animation plays once
    if (!active) return;

    el.querySelectorAll('[data-scb-pick]').forEach((input) => {
      const p = Number(input.value);
      const takenElsewhere = this.slots.some((other, k) => k < this.count && k !== i && other.p === p);
      input.checked = slot.p === p;
      input.disabled = takenElsewhere || !this.sellable(p);
    });

    const empty = el.querySelector('[data-scb-empty]');
    const stage = el.querySelector('[data-scb-stage]');
    if (slot.p < 0) {
      empty.hidden = false;
      stage.hidden = true;
      return;
    }
    empty.hidden = true;
    stage.hidden = false;

    if (el.dataset.built !== String(slot.p)) this.buildFields(i);
    this.updateFields(i);

    const { product, pool, complete, variant } = this.resolve(slot);
    const { currency, locale } = product;
    text(el.querySelector('[data-scb-title]'), product.title);
    this.setImage(el.querySelector('[data-scb-image]'), (variant && variant.image) || this.meta[slot.p].featured, variant ? variant.alt : product.title, first);

    const priceEl = el.querySelector('[data-scb-price]');
    if (complete && variant) {
      text(priceEl, variant.available ? variant.price_fmt : `${variant.price_fmt} · ${this.dataset.soldOut || 'Sold out'}`);
    } else {
      text(priceEl, this.rangeText(pool, currency, locale));
    }

    const link = el.querySelector('[data-scb-link]');
    if (link && variant) link.href = `${product.url}?variant=${variant.id}`;
  }

  /** "From $x" only when the options still open really lead to different prices. */
  rangeText(pool, currency, locale) {
    const prices = pool.map((candidate) => candidate.price);
    const low = Math.min(...prices);
    return `${Math.max(...prices) > low ? `${this.dataset.from || 'From'} ` : ''}${money(low, currency, locale)}`;
  }

  buildFields(i) {
    const el = this.slotEls[i];
    const slot = this.slots[i];
    const product = this.products[slot.p];
    const box = el.querySelector('[data-scb-fields]');
    box.textContent = '';
    product.options.forEach((option, k) => {
      if (SINGLE_OPTION(option)) return;
      const wrap = document.createElement('div');
      wrap.className = 'sc-select-wrap';
      const id = `scb-${this.uid}-${i}-${k}`;
      const label = document.createElement('label');
      label.htmlFor = id;
      label.textContent = option.name;
      const select = document.createElement('select');
      select.className = 'sc-select';
      select.id = id;
      select.dataset.scbOpt = String(k);
      if (REQUIRED_OPTION.test(option.name)) {
        select.add(new Option(`${this.dataset.choosePrefix || 'Choose'} ${option.name.toLowerCase()}`, ''));
      }
      option.values.forEach((value) => select.add(new Option(value, value)));
      wrap.append(label, select);
      box.append(wrap);
    });
    el.dataset.built = String(slot.p);
  }

  updateFields(i) {
    const slot = this.slots[i];
    const product = this.products[slot.p];
    this.slotEls[i].querySelectorAll('select[data-scb-opt]').forEach((select) => {
      const k = Number(select.dataset.scbOpt);
      Array.from(select.options).forEach((option) => {
        if (option.value === '') return;
        const probe = [...slot.sel];
        probe[k] = option.value;
        const ok = this.matching(product, probe).some((variant) => variant.available);
        option.disabled = !ok;
        option.textContent = ok ? option.value : `${option.value} – ${this.dataset.unavailable || 'unavailable'}`;
      });
      select.value = slot.sel[k] === null ? '' : slot.sel[k];
    });
  }

  setImage(img, src, alt, first) {
    if (!img || !src || img.dataset.src === src) return;
    const apply = () => {
      img.src = src;
      img.srcset = [300, 450, 600].map((w) => `${src.replace(/width=\d+/, `width=${w}`)} ${w}w`).join(', ');
      img.alt = alt || '';
      img.dataset.src = src;
      img.classList.remove('is-swapping');
    };
    if (first || reducedMotion.matches || !img.dataset.src) {
      apply();
      return;
    }
    img.classList.add('is-swapping');
    const probe = new Image();
    probe.onload = probe.onerror = () => window.setTimeout(apply, 120);
    probe.src = src;
  }

  renderPresets() {
    const active = this.slots.slice(0, this.count).map((slot) => (slot.p >= 0 ? this.products[slot.p].handle : ''));
    this.presets.forEach((button) => {
      const handles = button.dataset.scbPreset.split(',');
      const usable = handles.every((handle) => {
        const p = this.products.findIndex((product) => product.handle === handle);
        return p >= 0 && this.sellable(p);
      });
      button.disabled = !usable;
      button.setAttribute('aria-pressed', String(handles.length === active.length && handles.every((handle) => active.includes(handle))));
    });
  }

  renderSummary(first) {
    const { currency, locale } = this.products[0];
    const rows = this.rows();
    const ready = rows.every((row) => row && row.complete && row.variant && row.variant.available);

    while (this.listEl.children.length < rows.length) {
      const li = document.createElement('li');
      ['scb-sum__name', 'scb-sum__var', 'scb-sum__price'].forEach((name) => {
        const part = document.createElement('span');
        part.className = name;
        li.append(part);
      });
      this.listEl.append(li);
    }
    while (this.listEl.children.length > rows.length) this.listEl.lastElementChild.remove();

    let gap = '';
    rows.forEach((row, k) => {
      const li = this.listEl.children[k];
      const nameEl = li.children[0];
      const varEl = li.children[1];
      const priceEl = li.children[2];
      if (!row) {
        text(nameEl, `${this.dataset.piece || 'Piece'} ${k + 1}`);
        text(varEl, this.dataset.pickPiece || 'Choose a piece');
        varEl.classList.add('is-missing');
        text(priceEl, '');
        if (!gap) gap = this.dataset.pickPiece || 'Choose a piece';
        return;
      }
      const label = this.meta[this.slots[k].p].label || row.product.title;
      text(nameEl, label);
      if (row.complete && row.variant) {
        varEl.classList.remove('is-missing');
        text(varEl, row.product.options.length > 1 ? row.variant.title : row.variant.title === 'Default Title' ? row.product.title : row.variant.title);
        text(priceEl, row.variant.price_fmt);
      } else {
        const missing = this.slots[k].sel
          .map((value, index) => (value === null ? row.product.options[index].name.toLowerCase() : null))
          .filter(Boolean)
          .join(' & ');
        const note = `${this.dataset.choosePrefix || 'Choose'} ${missing}`;
        varEl.classList.add('is-missing');
        text(varEl, note);
        text(priceEl, this.rangeText(row.pool, currency, locale));
        if (!gap) gap = `${note} · ${label}`;
      }
    });

    const pct = this.pct[this.count] || 0;
    let regular = 0;
    let total = 0;
    if (ready) {
      rows.forEach((row) => {
        const price = row.variant.price;
        regular += price;
        total += price - Math.floor((price * pct) / 100);
      });
    }
    const saves = ready && total < regular;

    if (!first && this.priceBox && !reducedMotion.matches) {
      this.priceBox.classList.add('is-changing');
      window.setTimeout(() => this.priceBox.classList.remove('is-changing'), 180);
    }
    this.totalEl.hidden = !ready;
    if (ready) text(this.totalEl, money(total, currency, locale));
    this.regularEl.hidden = !saves;
    if (saves) text(this.regularEl, money(regular, currency, locale));
    this.saveEl.hidden = !saves;
    if (saves) {
      this.saveEl.textContent = (this.dataset.saveTemplate || 'You save %s').replace('%s', `${money(regular - total, currency, locale)} (${pct}%)`);
    }
    if (this.hintEl) {
      text(
        this.hintEl,
        ready
          ? ''
          : pct > 0
            ? `${gap}. ${(this.dataset.hintPct || 'Save %p% when your combo is complete.').replace('%p', String(pct))}`
            : gap
      );
    }

    this.addButton.setAttribute('aria-disabled', String(!ready));
    if (this.addLabel && !this.addButton.classList.contains('is-done')) {
      text(this.addLabel, ready ? this.addButton.dataset.label : this.addButton.dataset.choose || this.addButton.dataset.label);
    }

    const announce = ready ? `${this.count}-piece combo: ${money(total, currency, locale)}${saves ? `, you save ${money(regular - total, currency, locale)}` : ''}` : '';
    if (this.live && announce !== this.lastAnnounce && !this.addButton.classList.contains('is-done')) {
      this.lastAnnounce = announce;
      this.live.textContent = announce;
    }
  }
}

if (!customElements.get('sk-buy')) customElements.define('sk-buy', SkBuy);
if (!customElements.get('sk-pair')) customElements.define('sk-pair', SkPair);
if (!customElements.get('sk-combo')) customElements.define('sk-combo', SkCombo);
